import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { User, X, Loader2 } from 'lucide-react';

interface PersonaBrief {
  id: string;
  name: string;
  generated_brief: string | null;
}

interface PersonaSelectorProps {
  brandId: string;
  selectedPersonaIds: string[];
  onChange: (personaIds: string[]) => void;
}

export function PersonaSelector({ brandId, selectedPersonaIds, onChange }: PersonaSelectorProps) {
  const [briefs, setBriefs] = useState<PersonaBrief[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('');

  useEffect(() => {
    const fetchBriefs = async () => {
      if (!brandId) return;
      
      setIsLoading(true);
      const { data, error } = await supabase
        .from('personas_briefs')
        .select('id, name, generated_brief')
        .eq('brand_id', brandId)
        .order('name');
      
      if (error) {
        console.error('Error fetching personas briefs:', error);
        setIsLoading(false);
        return;
      }
      
      setBriefs(data || []);
      if (data && data.length > 0 && !activeTab) {
        setActiveTab(data[0].id);
      }
      setIsLoading(false);
    };

    fetchBriefs();
  }, [brandId]);

  const handleTogglePersona = (personaId: string) => {
    if (selectedPersonaIds.includes(personaId)) {
      onChange(selectedPersonaIds.filter(id => id !== personaId));
    } else {
      onChange([...selectedPersonaIds, personaId]);
    }
  };

  const handleRemovePersona = (personaId: string) => {
    onChange(selectedPersonaIds.filter(id => id !== personaId));
  };

  const getPersonaName = (personaId: string) => {
    return briefs.find(b => b.id === personaId)?.name || 'Unknown';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading personas...
      </div>
    );
  }

  if (briefs.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">
        No persona briefs found. Create persona briefs first to link them here.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Selected Personas Display */}
      {selectedPersonaIds.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedPersonaIds.map((personaId) => (
            <Badge key={personaId} variant="secondary" className="gap-1 pr-1">
              <User className="h-3 w-3" />
              {getPersonaName(personaId)}
              <button
                type="button"
                onClick={() => handleRemovePersona(personaId)}
                className="ml-1 hover:text-destructive rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Tabbed Brief View */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full flex-wrap h-auto gap-1 p-1">
          {briefs.map((brief) => (
            <TabsTrigger
              key={brief.id}
              value={brief.id}
              className="text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <div className="flex items-center gap-1.5">
                {selectedPersonaIds.includes(brief.id) && (
                  <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                )}
                {brief.name}
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        {briefs.map((brief) => (
          <TabsContent key={brief.id} value={brief.id} className="mt-3">
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm">{brief.name}</h4>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`persona-${brief.id}`}
                    checked={selectedPersonaIds.includes(brief.id)}
                    onCheckedChange={() => handleTogglePersona(brief.id)}
                  />
                  <label
                    htmlFor={`persona-${brief.id}`}
                    className="text-sm cursor-pointer"
                  >
                    Select
                  </label>
                </div>
              </div>
              
              {brief.generated_brief ? (
                <ScrollArea className="h-32 w-full rounded border bg-muted/30 p-3">
                  <div className="text-xs text-muted-foreground whitespace-pre-wrap">
                    {brief.generated_brief.substring(0, 500)}
                    {brief.generated_brief.length > 500 && '...'}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-xs text-muted-foreground italic py-4 text-center bg-muted/30 rounded">
                  No generated brief available
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
